<style>
  /* Undo Bootstrap styles */
  label {
    font-weight: initial;
    margin-bottom: initial;
  }
</style>

<template>
  <v-container
    class="pa-0"
  >
    <v-layout
      align-center
      wrap
    >
      <v-flex
        xs12
        class="py-0"
      >
        <v-data-table
          :headers="headers"
          :items="compliance"
          :items-per-page="15"
          :loading="loading"
          :search="search"
          :sort-by="['nonCompliance', 'name']"
          class="elevation-1"
        >
          <template v-slot:top>
            <v-toolbar flat>
              <v-btn
                color="secondary"
                class="mr-2"
                :disabled="!canUpdateCompliance"
                @click="newComplianceItem()"
              >
                <v-icon left>
                  fa-plus
                </v-icon>
                New Compliance
              </v-btn>
              <v-spacer />
              <v-text-field
                v-model="search"
                append-icon="fa-search"
                label="Search"
                hide-details
                single-line
              />
            </v-toolbar>
          </template>

          <template v-slot:item.rating="{ item }">
            {{ item.rating | textFrom(threatRatings) }}
          </template>

          <template v-slot:item.nonCompliance="{ item }">
            <v-checkbox
              class="ma-0"
              hide-details
              disabled
              :input-value="item.nonCompliance"
            />
          </template>

          <template v-slot:item.action="{ item }">
            <v-btn
              color="primary lighten-2"
              small
              text
              :disabled="!canUpdateCompliance"
              @click="editComplianceItem(item)"
            >
              <v-icon left>
                fa-edit
              </v-icon>
              edit
            </v-btn>
            <v-btn
              color="warning"
              small
              text
              :disabled="!canUpdateCompliance"
              @click.stop="(deleting.dialog = true) && (deleting.complianceItem = item)"
            >
              <v-icon left>
                fa-remove
              </v-icon>
              delete
            </v-btn>
          </template>
        </v-data-table>
      </v-flex>
    </v-layout>

    <v-dialog
      v-model="editing.dialog"
      max-width="600px"
      persistent
    >
      <v-card v-if="editing.complianceItem">
        <v-card-title>
          <span class="headline">Editing {{ editing.complianceItem.name }}</span>
          <v-spacer />
          <v-btn
            icon
            @click="editing.dialog = false"
          >
            <v-icon>fa-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text v-if="!editing.inProgress">
          <v-form v-model="editing.valid">
            <v-container grid-list-lg>
              <v-layout wrap>
                <v-flex
                  xs12
                >
                  <v-text-field
                    v-if="editing.dialog"
                    v-model="editing.complianceItem.name"
                    :counter="200"
                    :rules="[validation.required, validation.maxLength200]"
                    autofocus
                    label="Compliance item name"
                    required
                  />
                </v-flex>

                <v-flex
                  xs4
                  md3
                >
                  <v-checkbox
                    v-model="editing.complianceItem.nonCompliance"
                    label="Negative"
                  />
                </v-flex>

                <v-flex
                  xs8
                  md9
                >
                  <v-select
                    v-model="editing.complianceItem.rating"
                    :items="threatRatings"
                    label="Threat Rating"
                  />
                </v-flex>
              </v-layout>
            </v-container>
          </v-form>
        </v-card-text>

        <v-card-text v-if="editing.inProgress">
          Please stand by
          <v-progress-linear
            indeterminate
            color="primary"
          />
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            :disabled="editing.inProgress || !editing.valid"
            color="accent"
            @click="saveComplianceItem(editing.complianceItem)"
          >
            Save
          </v-btn>

          <v-btn
            :disabled="editing.inProgress"
            @click="editing.dialog = false"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="deleting.dialog"
      max-width="600px"
    >
      <v-card v-if="deleting.complianceItem">
        <v-card-title class="headline">
          Deleting {{ deleting.complianceItem.name }}
        </v-card-title>

        <v-card-text v-if="!deleting.inProgress">
          Are you sure you want to delete this compliance item?
        </v-card-text>

        <v-card-text v-if="deleting.inProgress">
          Please stand by
          <v-progress-linear
            indeterminate
            color="primary"
          />
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            :disabled="deleting.inProgress"
            color="warning"
            @click="deleteComplianceItem(deleting.complianceItem)"
          >
            Yes
          </v-btn>

          <v-btn
            :disabled="deleting.inProgress"
            @click="deleting.dialog = false"
          >
            No
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.show"
      bottom
      color="error"
      multi-line
    >
      {{ snackbar.text }}
      <v-btn
        dark
        text
        @click="snackbar.show = false"
      >
        Close
      </v-btn>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from 'axios';

export default {
  components: {
  },

  filters: {
    textFrom(value, list) {
      // Search the array for an item with the given value key and return its text
      // Allows a single array for presenting human readable values in data rows, radios and selects
      const item = list.find(e => e.value === value);
      return item ? item.text : 'Not Found';
    },
  },

  data() {
    return {
      loading: false,

      // TODO: These values should all be in the Vuex store.
      companyId: window.companyId,
      canReadCompliance: window.canReadCompliance,
      canUpdateCompliance: window.canUpdateCompliance,
      compliance: [],

      search: '', // Data table filter
      headers: [ // Data table columns
        { text: 'Negative', value: 'nonCompliance' },
        { text: 'Name', value: 'name' },
        { text: 'Threat Rating', value: 'rating' },
        {
          align: 'right',
          sortable: false,
          text: 'Actions',
          value: 'action',
        },
      ],

      threatRatings: [
        { value: 1, text: 'No Warning' },
        { value: 2, text: 'Normal' },
        { value: 3, text: 'Important' },
        { value: 4, text: 'Prevent Temporary Placement' },
        { value: 5, text: 'Prevent Any Placement' },
      ],

      editing: {
        dialog: false, // Show the complianceItem edit modal dialog when true
        inProgress: false, // Show progress bar instead of the dialog contents, disable actions
        complianceItem: null, // The compliance item to edit/create
        valid: null,
      },

      validation: {
        required: value => !!value || 'Required.',
        maxLength200: value => value.length <= 200 || 'Max 200 characters.',
      },

      deleting: {
        dialog: false,
        inProgress: false,
        complianceItem: null,
      },

      snackbar: {
        show: false,
        text: '',
      },
    };
  },

  computed: {
  },

  mounted() {
    this.loadCompliance();
  },

  methods: {
    async loadCompliance() {
      this.loading = true;
      try {
        const response = await axios({
          method: 'GET',
          url: '/api/compliance/',
          params: {
            companyId: this.companyId,
            limit: 250,
          },
        });
        this.compliance = this.convertCompliance(response.data.data);
      } catch (error) {
        // TODO: Need to touch/copy this code? Make the snackbar to be a component.
        if (error.response) {
          this.snackbar.text = `Error: ${error.response.status} in ${error.request.responseURL}. Please try refreshing the page, or contact us if the problem persists.`;
        } else {
          this.snackbar.text = 'An unknown error occurred. Please try refreshing the page, or contact us if the problem persists.';
        }
        this.snackbar.show = true;
      } finally {
        this.loading = false;
      }
    },

    convertCompliance(compliance) {
      return compliance.map((complianceItem) => {
        const outComplianceItem = Object.assign({}, complianceItem);
        // Nothing to do here. All values go back and forth to the API unchanged.
        return outComplianceItem;
      });
    },

    newComplianceItem() {
      this.editing.complianceItem = {
        name: 'New compliance',
        rating: 2, // Normal a.k.a. small warning
        nonCompliance: false,
      };
      // Show the editor
      this.editing.dialog = true;
    },

    editComplianceItem(complianceItem) {
      // Create a copy of the existing compliance item for editing.
      this.editing.complianceItem = Object.assign({}, complianceItem);
      this.editing.dialog = true;
    },

    async saveComplianceItem(complianceItem) {
      this.editing.inProgress = true;
      try {
        if (complianceItem.id) { // Update existing
          await axios({
            method: 'PUT',
            url: `/api/compliance/${complianceItem.id}`,
            params: complianceItem,
          });
        } else { // Create new
          await axios({
            method: 'POST',
            url: '/api/compliance/',
            data: complianceItem,
          });
        }
      } catch (error) {
        // TODO: Need to touch/copy this code? Make the snackbar to be a component.
        if (error.response) {
          this.snackbar.text = `Error: ${error.response.status} in ${error.request.responseURL}. Please try refreshing the page, or contact us if the problem persists.`;
        } else {
          this.snackbar.text = 'An unknown error occurred. Please try refreshing the page, or contact us if the problem persists.';
        }
        this.snackbar.show = true;
      } finally {
        // Close the editor modal
        this.editing.dialog = false;
        this.editing.inProgress = false;
        this.loadCompliance();
      }
    },

    async deleteComplianceItem(complianceItem) {
      this.deleting.inProgress = true;
      try {
        await axios({
          method: 'DELETE',
          url: `/api/compliance/${complianceItem.id}`,
        });
      } catch (error) {
        // TODO: Need to touch/copy this code? Make the snackbar to be a component.
        if (error.response) {
          this.snackbar.text = `Error: ${error.response.status} in ${error.request.responseURL}. Please try refreshing the page, or contact us if the problem persists.`;
        } else {
          this.snackbar.text = 'An unknown error occurred. Please try refreshing the page, or contact us if the problem persists.';
        }
        this.snackbar.show = true;
      } finally {
        this.deleting.dialog = false;
        this.deleting.inProgress = false;
        this.loadCompliance();
      }
    },
  },
};
</script>
